import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
  OnDestroy,
  output,
  signal,
} from '@angular/core';

import { LOCALE, TranslatePipe } from '@core/i18n-legacy';
import {
  ConnectedOverlayDirective,
  DotComponent,
  DropdownComponent,
  DropdownItemComponent,
  DropdownWidth,
  IconComponent,
  OverlayOriginDirective,
} from '@core/user-interface-legacy';

import { TellentAppShellTopbarButtonComponent } from '../tellent-app-shell-topbar-button/tellent-app-shell-topbar-button.component';

const HELP_CENTER_URLS: Record<string, string> = {
  en: 'https://support.tellent.com',
  de: 'https://support.tellent.com/de/',
  fr: 'https://support.tellent.com/fr/',
  nl: 'https://support.tellent.com/nl/',
};

const CONTACT_SALES_URLS: Record<string, string> = {
  en: 'https://tellent.com/contact-us',
  de: 'https://tellent.com/de/kontakt',
  fr: 'https://tellent.com/fr/contact',
  nl: 'https://tellent.com/nl/contact',
};

@Component({
  selector: 'tlc-help-dropdown',
  templateUrl: './help-dropdown.component.html',
  styleUrl: './help-dropdown.component.less',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    DotComponent,
    DropdownComponent,
    DropdownItemComponent,
    IconComponent,
    ConnectedOverlayDirective,
    OverlayOriginDirective,
    TranslatePipe,
    TellentAppShellTopbarButtonComponent,
  ],
})
export class HelpDropdownComponent implements OnDestroy {
  protected readonly DropdownWidth = DropdownWidth;

  readonly #locale = inject(LOCALE);

  public helpCenterUrl = input<string | undefined>(undefined);
  public productUpdatesUrl = input('https://updates.tellent.com');
  public shareFeedbackUrl = input('https://feedback.tellent.com');
  public showGetStarted = input(true);
  public showContactSupport = input(true);
  public showContactSales = input(true);
  public showCopyDebugInfo = input(false);
  public isActive = input(false);
  public isUnread = input(false);

  public chatWithUs = output<void>();
  public getStarted = output<void>();
  public productUpdatesClick = output<void>();
  public copyDebugInfo = output<void>();

  protected resolvedHelpCenterUrl = computed(
    () => this.helpCenterUrl() ?? HELP_CENTER_URLS[this.#locale] ?? HELP_CENTER_URLS['en'],
  );

  protected contactSalesUrl = CONTACT_SALES_URLS[this.#locale] ?? CONTACT_SALES_URLS['en'];
  protected debugInfoCopied = signal(false);
  #debugInfoTimeout: ReturnType<typeof setTimeout> | null = null;

  public ngOnDestroy(): void {
    if (this.#debugInfoTimeout) {
      clearTimeout(this.#debugInfoTimeout);
    }
  }

  protected onProductUpdatesClick(dropdown: ConnectedOverlayDirective): void {
    dropdown.open = false;
    this.productUpdatesClick.emit();
  }

  protected onGetStarted(dropdown: ConnectedOverlayDirective): void {
    dropdown.open = false;
    this.getStarted.emit();
  }

  protected onChatWithUs(dropdown: ConnectedOverlayDirective): void {
    dropdown.open = false;
    this.chatWithUs.emit();
  }

  protected onCopyDebugInfo(): void {
    this.copyDebugInfo.emit();
    this.debugInfoCopied.set(true);
    if (this.#debugInfoTimeout) {
      clearTimeout(this.#debugInfoTimeout);
    }
    this.#debugInfoTimeout = setTimeout(() => {
      this.debugInfoCopied.set(false);
      this.#debugInfoTimeout = null;
    }, 2000);
  }
}
